import React, { useState, useEffect } from 'react';
import StudentLayout from '../../components/StudentLayout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Star as StarIcon,
  Event as EventIcon,
  MusicNote as MusicIcon,
  School as WorkshopIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import axios from 'axios';

const StudentEvents = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/student/events', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data.events || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading events');
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (type) => {
    const icons = {
      arangetram: <StarIcon />,
      annual_day: <EventIcon />,
      performance: <MusicIcon />,
      workshop: <WorkshopIcon />,
      other: <EventIcon />
    };
    return icons[type] || <EventIcon />;
  };

  const getEventColor = (type) => {
    const colors = {
      arangetram: 'error',
      annual_day: 'primary',
      performance: 'secondary',
      workshop: 'info',
      other: 'default'
    };
    return colors[type] || 'default';
  };

  const isUpcoming = (eventDate) => {
    return new Date(eventDate) > new Date();
  };

  if (loading) {
    return (
      <StudentLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <Box>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Events & Performances
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Upcoming and past events at Chinmudra Academy
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {events.length === 0 ? (
          <Card sx={{
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
            border: '1px solid',
            borderColor: 'rgba(212, 175, 55, 0.2)',
            boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '4px',
              height: '100%',
              background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
            },
          }}>
            <CardContent sx={{ pl: 3 }}>
              <Typography variant="body1" sx={{ color: '#8B7355' }} align="center" py={4}>
                No events scheduled at this time.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {events.map((event) => (
              <Grid item xs={12} md={6} key={event.id}>
                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
                    border: isUpcoming(event.event_date) ? '2px solid' : '1px solid',
                    borderColor: isUpcoming(event.event_date) ? '#D4AF37' : 'rgba(212, 175, 55, 0.2)',
                    boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(212, 175, 55, 0.25)',
                      transform: 'translateY(-2px)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '4px',
                      height: '100%',
                      background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
                    },
                  }}
                >
                  <CardContent sx={{ pl: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      {getEventIcon(event.event_type)}
                      <Chip
                        label={event.event_type.replace('_', ' ')}
                        size="small"
                        color={getEventColor(event.event_type)}
                      />
                      {isUpcoming(event.event_date) && (
                        <Chip label="Upcoming" size="small" color="success" />
                      )}
                    </Box>

                    <Typography variant="h5" fontWeight={600} sx={{ color: '#2C2416' }} gutterBottom>
                      {event.title}
                    </Typography>

                    {event.description && (
                      <Typography variant="body2" sx={{ color: '#8B7355' }} paragraph>
                        {event.description}
                      </Typography>
                    )}

                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {new Date(event.event_date).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            weekday: 'long'
                          })}
                        </Typography>
                      </Box>

                      {event.venue && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2">{event.venue}</Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </StudentLayout>
  );
};

export default StudentEvents;
